import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { UserPreferences } from '../types/userPreferences';

export type {
  UserPreferences,
  DashboardPreferences,
  ShipmentsColumnPreferences,
} from '../types/userPreferences';

// Default column order for shipments table
export const DEFAULT_SHIPMENT_COLUMNS = [
  'sn',
  'product',
  'status',
  'supplier',
  'origin',
  'pol',
  'pod',
  'eta',
  'container_count',
  'weight',
  'total_price',
];

// All available columns with display names (resolve via t(labelKey, label))
export const SHIPMENT_COLUMN_CONFIG: Record<
  string,
  { key: string; labelKey: string; label: string }
> = {
  sn: { key: 'sn', labelKey: 'shipments.columns.sn', label: 'Shipment No.' },
  product: { key: 'product', labelKey: 'shipments.columns.product', label: 'Type of Goods' },
  price_per_ton: {
    key: 'price_per_ton',
    labelKey: 'shipments.columns.pricePerTon',
    label: 'Price per Ton',
  },
  origin: { key: 'origin', labelKey: 'shipments.columns.origin', label: 'Origin' },
  pol: { key: 'pol', labelKey: 'shipments.columns.pol', label: 'POL' },
  pod: { key: 'pod', labelKey: 'shipments.columns.pod', label: 'POD' },
  status: { key: 'status', labelKey: 'shipments.columns.status', label: 'Status' },
  total_price: {
    key: 'total_price',
    labelKey: 'shipments.columns.totalPrice',
    label: 'Total Price',
  },
  price_on_paper: {
    key: 'price_on_paper',
    labelKey: 'shipments.columns.priceOnPaper',
    label: 'Price on Paper',
  },
  tax: { key: 'tax', labelKey: 'shipments.columns.tax', label: 'Tax' },
  eta: { key: 'eta', labelKey: 'shipments.columns.eta', label: 'ETA' },
  etd: { key: 'etd', labelKey: 'shipments.columns.etd', label: 'ETD' },
  supplier: { key: 'supplier', labelKey: 'shipments.columns.supplier', label: 'Supplier' },
  buyer: { key: 'buyer', labelKey: 'shipments.columns.buyer', label: 'Importer' },
  packaging_type: {
    key: 'packaging_type',
    labelKey: 'shipments.columns.packagingType',
    label: 'Packaging Type',
  },
  packaging_count: {
    key: 'packaging_count',
    labelKey: 'shipments.columns.packagingCount',
    label: 'Packaging Qty',
  },
  customer: { key: 'customer', labelKey: 'shipments.columns.customer', label: 'Customer' },
  container_count: {
    key: 'container_count',
    labelKey: 'shipments.columns.containerCount',
    label: 'Containers',
  },
  weight: { key: 'weight', labelKey: 'shipments.columns.weight', label: 'Weight (MT)' },
  bl_no: { key: 'bl_no', labelKey: 'shipments.columns.blNo', label: 'BL No.' },
  vessel: { key: 'vessel', labelKey: 'shipments.columns.vessel', label: 'Vessel' },
  final_destination: {
    key: 'final_destination',
    labelKey: 'shipments.columns.finalDestination',
    label: 'Final Destination',
  },
};

// Fetch current user's preferences
async function fetchMyPreferences(): Promise<UserPreferences> {
  const response = await apiClient.get('/auth/me/preferences');
  return response.data.preferences || {};
}

// Update current user's preferences
async function updateMyPreferences(preferences: UserPreferences): Promise<UserPreferences> {
  const response = await apiClient.put('/auth/me/preferences', { preferences });
  return response.data.preferences;
}

// Fetch a specific user's preferences (admin only)
async function fetchUserPreferences(userId: string): Promise<UserPreferences> {
  const response = await apiClient.get(`/auth/users/${userId}/preferences`);
  return response.data.preferences || {};
}

// Update a specific user's preferences (admin only)
async function updateUserPreferences(
  userId: string,
  preferences: UserPreferences
): Promise<UserPreferences> {
  const response = await apiClient.put(`/auth/users/${userId}/preferences`, { preferences });
  return response.data.user.ui_preferences;
}

/**
 * Hook to fetch and manage current user's UI preferences
 */
export function useUserPreferences() {
  const queryClient = useQueryClient();
  const { user, isAuthenticated } = useAuth();
  const sessionPreferences = user?.ui_preferences;

  const {
    data: preferences,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['user-preferences', user?.id],
    queryFn: fetchMyPreferences,
    enabled: isAuthenticated && !!user?.id,
    initialData: sessionPreferences,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const resolvedPreferences = preferences ?? sessionPreferences ?? {};

  const updateMutation = useMutation({
    mutationFn: updateMyPreferences,
    onSuccess: newPreferences => {
      queryClient.setQueryData(['user-preferences', user?.id], newPreferences);
    },
  });

  // Helper to check if tasks should be hidden on dashboard
  const shouldHideTasks = (): boolean => {
    return resolvedPreferences?.dashboard?.hide_tasks === true;
  };

  // Get column order for shipments table
  const getShipmentColumns = (): string[] => {
    const order = resolvedPreferences?.shipments_columns?.order;
    if (order && order.length > 0) {
      return order;
    }
    return DEFAULT_SHIPMENT_COLUMNS;
  };

  // Get hidden columns for shipments table
  const getHiddenColumns = (): string[] => {
    return resolvedPreferences?.shipments_columns?.hidden || [];
  };

  return {
    preferences: resolvedPreferences,
    isLoading: isLoading && !sessionPreferences,
    error,
    updatePreferences: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    shouldHideTasks,
    getShipmentColumns,
    getHiddenColumns,
  };
}

/**
 * Hook for admins to manage other users' preferences
 */
export function useAdminUserPreferences(userId: string | null) {
  const queryClient = useQueryClient();

  const {
    data: preferences,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['user-preferences', userId],
    queryFn: () => fetchUserPreferences(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });

  const updateMutation = useMutation({
    mutationFn: (newPreferences: UserPreferences) => updateUserPreferences(userId!, newPreferences),
    onSuccess: newPreferences => {
      queryClient.setQueryData(['user-preferences', userId], newPreferences);
      // Also invalidate the users list to refresh any cached data
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  return {
    preferences: preferences || {},
    isLoading,
    error,
    updatePreferences: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}
